import dates from 'src/utils/dates'
import { createReportId } from 'src/report/store/utils/report'

import Networks from './networks'

const user = {
  defaults: {
    access: {
      active: false,
      expires: '2200-01-01T00:00:00',
      level: null,
      permissions: {
        alias: false,
        bulk: false,
        customAnalytics: false,
        demo: false,
        dev: false,
        discrepancy: false,
        pacing: false,
        programmatic: false,
        settings: false,
      },
      role: null,
    },
    network: [],
    email: null,
    settings: {
      alias: {},
      bookmarks: {},
      dashboard: {},
      demo: {},
      filters: [],
      home: {},
      messages: {},
      networks: [],
      reports: [],
      themes: {
        dark: false,
      },
    },
  },

  validate: {
    network(network) {
      return Array.isArray(network) && !network.find((n) => {
        const hasValidOrganization = () => {
          const { organization } = n || {}
          return organization && typeof organization === 'object' && organization.code && organization.id
        }
        const hasValidTeam = () => {
          const { team } = n || {}
          return Array.isArray(team) && !team.find((t) => !t.id || !t.code)
        }
        return !hasValidOrganization() || !hasValidTeam()
      })
    },
    person(personUser) {
      const { isActive, isDeleted, personId } = personUser || {}
      const isExistingPersonRecord = () => !!personId || 'person/not-found'
      const hasValidIsActive = () => typeof isActive === 'boolean' || 'person/is-active'
      const hasValidIsDeleted = () => typeof isDeleted === 'boolean' || 'person/is-deleted'
      const hasFirstName = () => {
        const { personNameFirst } = personUser
        return (
          typeof personNameFirst === 'string' && personNameFirst.trim().length > 0
        ) || 'person/name-first'
      }
      const hasLastName = () => {
        const { personNameLast } = personUser
        return (
          typeof personNameLast === 'string' && personNameLast.trim().length > 0
        ) || 'person/name-last'
      }
      return [
        isExistingPersonRecord,
        hasValidIsActive,
        hasValidIsDeleted,
        hasFirstName,
        hasLastName,
      ]
    },
    user(personUser) {
      const { access, docId, network } = personUser || {}
      const { active, expires, level, role } = access || {}

      const isExistingUserRecord = () => !!docId || 'user/not-found'
      const hasValidAccess = () => typeof access === 'object' || 'user/access'
      const hasValidAccessActive = () => typeof active === 'boolean' || 'user/access-active'
      const hasValidAccessLevel = () => ['reader', 'writer', 'admin'].includes(level) || 'user/access-level'
      const hasValidAccessRole = () => ['external', 'internal'].includes(role) || 'user/access-role'

      const hasNetworkDefined = () => Array.isArray(network) || 'user/network-undefined'
      const hasNetworkConfigured = () => this.network(network) || 'user/network-config'

      const validateExpires = () => !expires || typeof expires === 'string'
      const hasValidExpires = () => validateExpires() || 'user/access-expires'

      return [
        isExistingUserRecord,
        hasValidAccess,
        hasValidAccessActive,
        hasValidAccessLevel,
        hasValidAccessRole,
        hasValidExpires,
        hasNetworkDefined,
        hasNetworkConfigured,
      ]
    },
  },
  validateRules(personUser, validators) {
    return validators.reduce((errors, validator) => {
      const validationResult = validator()
      if (validationResult !== true) errors.push(validationResult)
      return errors
    }, [])
  },
  validatePerson(personUser) {
    const validators = this.validate.person(personUser)
    return this.validateRules(personUser, validators)
  },
  validateUser(personUser) {
    const validators = this.validate.user(personUser)
    return this.validateRules(personUser, validators)
  },

  authorize: {
    person(record) {
      const { isActive, isDeleted, personId } = record || {}
      const hasPersonId = () => !!personId || 'person/auth-id'
      const isPersonActive = () => isActive || 'person/auth-inactive'
      const notPersonDeleted = () => !isDeleted || 'person/auth-deleted'

      return [
        hasPersonId,
        isPersonActive,
        notPersonDeleted,
      ]
    },
    user(record) {
      const { access, docId } = record || {}
      const { active, expires } = access || {}

      const validateEmail = (email) => typeof email === 'string' && !!email.length
      const hasDocId = () => validateEmail(docId) || 'user/auth-id'
      const isActive = () => active || 'user/auth-inactive'
      const notExpired = () => {
        const validates = () => !expires || dates.areDatesGte(expires, dates.now())
        return validates() || 'user/auth-expired'
      }

      return [
        hasDocId,
        isActive,
        notExpired,
      ]
    },
  },
  authorizePerson(personUser) {
    const validators = this.authorize.person(personUser)
    return this.validateRules(personUser, validators)
  },
  authorizeUser(personUser) {
    const validators = this.authorize.user(personUser)
    return this.validateRules(personUser, validators)
  },
  isAuthorizedPerson(personUser) {
    return this.authorizePerson(personUser).length === 0
  },
  isAuthorizedUser(personUser) {
    return this.authorizeUser(personUser).length === 0
  },

  isActiveUser(personUser) {
    const isActiveUser = () => {
      const { access, isActive } = personUser || { access: {} }
      const { active = false, role = 'external' } = access || {}

      const isActiveInternal = () => role === 'internal' && active && isActive
      const isActiveExternal = () => role === 'external' && active && isActive

      return isActiveInternal() || isActiveExternal()
    }

    return !!personUser && isActiveUser()
  },
  isInternalUser(personUser) {
    const isInternalUser = () => {
      const isActiveUser = this.isActiveUser(personUser)
      const { access, isInternal } = personUser
      const { role } = access || {}

      return isActiveUser && role === 'internal' && isInternal
    }

    return !!personUser && isInternalUser()
  },

  makeDemoNetworks() {
    const organization = { code: 'DEMO2021', id: 10, networkCode: 8788 }
    const team = [{ id: '-1', code: '-1' }]

    return [{ organization, team }]
  },
  inDemoMode(userRecord) {
    const { access = {}, settings = {} } = userRecord
    const { role } = access
    const isInternalUser = role === 'internal'
    const { demo: demoPermissions } = settings || {}
    const { demo: demoSettings } = settings || {}
    const hasDemoPermissionsOn = () => demoPermissions && demoPermissions === true
    const isDemoExternal = !isInternalUser && hasDemoPermissionsOn() && demoSettings && (
      demoSettings === true || (
        typeof demoSettings === 'object' && demoSettings.active
      )
    )
    const inDemoMode = isInternalUser && demoSettings && (
      demoSettings === true || (
        typeof demoSettings === 'object' && demoSettings.active
      )
    )

    return isDemoExternal || inDemoMode || false
  },

  getNetworks(isInternalUser, networkPermissions, networkSettings) {
    const useNetworkPermissions = () => {
      const normalizeNetworkTeams = (network) => {
        const associationId = network && network.organization && network.organization.code
        return network && Array.isArray(network.team) && network.team.map((team) => {
          const subId = (
            team && team.id
          ) || null
          return { associationId, subId }
        })
      }
      const mapNetworkPermissions = () => {
        const haveNetworkPermissions = Array.isArray(networkPermissions) && networkPermissions.length > 0
        const getNetworkPermissions = () => networkPermissions.reduce((flattenedNetworkTeams, network) => {
          const networkTeams = !network.associationId ? normalizeNetworkTeams(network) : [network]

          if (networkTeams) {
            networkTeams.forEach((networkTeam) => flattenedNetworkTeams.push(networkTeam))
          }

          return flattenedNetworkTeams
        }, [])
        const makeDemoNetworks = () => {
          const [demoNetwork] = this.makeDemoNetworks()
          return normalizeNetworkTeams(demoNetwork)
        }
        return (
          haveNetworkPermissions && getNetworkPermissions()
        ) || makeDemoNetworks() || []
      }

      return mapNetworkPermissions()
    }
    const useAllowedSettings = () => {
      const allowedSettings = networkSettings?.length
        > 0
        && networkPermissions?.length
        > 0
        && networkSettings.reduce((allowedSettingsArray, networkSetting) => {
          const { associationId } = networkSetting
          // @TODO Test the subId treatment here to assert its reference to the team.id.
          const isAllowedSetting = networkPermissions.find((n) => n.organization
            && n.organization.code
            === associationId
            && Array.isArray(n.team)
            && n.team.find((t) => t.id === networkSetting.subId))

          if (isAllowedSetting) {
            allowedSettingsArray.push(networkSetting)
          }

          return allowedSettingsArray
        }, [])

      return allowedSettings?.length > 0 && allowedSettings
    }
    const getInternalNetworkSettings = () => (
      (
        networkSettings?.length > 0 && networkSettings
      ) || useNetworkPermissions()
    )
    const useNetworkSettings = () => isInternalUser && getInternalNetworkSettings()

    return useNetworkSettings() || useAllowedSettings() || useNetworkPermissions() || []
  },
  /**
   * Gets a valid representation of a user's network permissions.
   * @param {Object<{network: Object[]}>} userRecord
   * @returns {Object[]} networks
   */
  getAssignedNetworks(userRecord) {
    const { network: userNetworks } = userRecord || {}
    const isValidNetworkDefined = Array.isArray(userNetworks) && !userNetworks.find((network) => typeof network
      !== 'object')
    return (
      isValidNetworkDefined && userNetworks
    ) || []
  },
  /**
   * Gets a valid representation of a user's allowed networks based on demo mode.
   * @param inDemoMode
   * @param personUser
   * @returns {[{organization: {code: string, networkCode: number, id: number}, team: [{code: string, id:
   *   string}]}]|Object[]}
   */
  getAllowedNetworks(inDemoMode, personUser) {
    const getAssignedNetworks = () => {
      const networkPermissions = this.getAssignedNetworks(personUser)
      const networkSettings = personUser.settings && personUser.settings.networks
      const isInternalUser = this.isInternalUser(personUser)

      return !!personUser && this.getNetworks(isInternalUser, networkPermissions, networkSettings)
    }

    return inDemoMode ? this.makeDemoNetworks() : getAssignedNetworks()
  },
  /**
   * For each of a user's allowed networks, finds and maps a publisher org-team onto the network's publisher property.
   * @param {boolean} inDemoMode
   * @param {Object} personUser
   * @param {Array} publishersAsTeams
   * @returns {Object[]}
   */
  mapPublisherTeamToNetwork(inDemoMode, personUser, publishersAsTeams) {
    const allowedNetworks = this.getAllowedNetworks(inDemoMode, personUser)
    return allowedNetworks.map((network) => Networks.mapPublisherTeamToNetwork(publishersAsTeams)(network))
  },

  /**
   * Modernizes legacy queries on older user records
   * @param {Object} network { defaults: Boolean, reports: String[], type: String? }
   * @param {String[]} reports ['discrepancy', 'pacing']
   * @returns {Object} { defaults: Boolean, reports: String[] }
   */
  modernizeLegacySavedFiltersToV2(network, reports) {
    if (!network.reports) {
      network.reports = reports || []
    } else if (Array.isArray(reports)) {
      reports.concat(network.reports)
    }
    return network
  },
  /**
   * Replaces a single filter setting's parameters property with the modern filterBy property.
   * @param filterSetting A legacy filter setting
   * @returns {*}
   */
  modernizeLegacyFilterSettingToV2(filterSetting) {
    const { filterBy, parameters } = filterSetting
    if (!filterBy) filterSetting.filterBy = parameters || {}
    return filterSetting
  },

  /**
   * Determines whether the user has prefilters for the current selected search's report.
   * @param {Object[]} legacyFilterSettings [{ name: String, network: [{ defaults, organizationId, teamId ],
   *   parameters: {}, reports: String[] }]
   * @return {Object} Modernized query settings
   */
  getLegacySavedFilters(legacyFilterSettings = []) {
    const invalidInput = !Array.isArray(legacyFilterSettings)

    return invalidInput && legacyFilterSettings.reduce((modernFilterSettings, filterSetting) => {
      const normalizedFilterSetting = user.modernizeLegacyFilterSettingToV2(filterSetting)
      const { name, filterBy, network: networks, reports: legacyReportsProp } = normalizedFilterSetting

      const modernizeLegacyFilterSetting = (network) => {
        const normalizedNetwork = user.modernizeLegacySavedFiltersToV2(network, legacyReportsProp)
        const { billingCode, reports, teamId } = normalizedNetwork
        const parameters = Object.keys(filterBy).map((legacyFilterKey) => {
          const legacyFilter = filterBy[legacyFilterKey]

          return {
            action: 'filterBy',
            operator: legacyFilter.operator,
            propertyName: legacyFilterKey,
            value: legacyFilter.value,
          }
        })

        return reports.map((baseReportName) => (
          {
            associationId: billingCode,
            baseReportName,
            columns: [],
            defaultRole: 'editor',
            isArchived: false,
            isClientEnforced: true,
            isFavorite: true,
            isMetric: false,
            parameters,
            reportLabel: name,
            reportPath: createReportId(),
            roles: [],
            scope: 'local',
            shares: [],
            subId: teamId,
          }
        ))
      }

      networks.forEach((network) => {
        const networkFilterSettings = modernizeLegacyFilterSetting(network)
        networkFilterSettings.forEach((networkFilterSetting) => {
          modernFilterSettings.push(networkFilterSetting)
        })
      })

      return modernFilterSettings
    }, [])
  },

  /* eslint-disable-next-line complexity */
  normalizeSettings(assignedSettings) {
    const {
      alias: definedAlias = {},
      bookmarks: definedBookmarks = {},
      dashboard: definedDashboard = {},
      demo: definedDemo = {},
      dev: definedDev = {},
      filters: definedFilters = [],
      home: definedHome = {},
      messages: definedMessages = {},
      networks: definedNetworks = [],
      reports: definedReports = [],
      themes: definedThemes = {},
    } = assignedSettings
    const hasValidAlias = typeof definedAlias === 'object'
    const hasValidBookmarks = Array.isArray(definedBookmarks) && definedBookmarks.length
    const hasValidDashboard = typeof definedDashboard === 'object'
    const hasValidDemo = typeof definedDemo === 'object'
    const hasValidDev = typeof definedDev === 'object'
    const hasValidHome = typeof definedHome === 'object'
    const hasValidMessages = typeof definedMessages === 'object'
    const hasValidNetworks = typeof definedNetworks === 'object'
    const hasValidReports = typeof definedReports === 'object'
    const hasValidThemes = typeof definedThemes === 'object'
    const hasValidUserFilters = Array.isArray(definedFilters) && definedFilters.length

    return {
      alias: (
        hasValidAlias && definedAlias
      ) || {},
      bookmarks: (
        hasValidBookmarks && definedBookmarks
      ) || [],
      dashboard: (
        hasValidDashboard && definedDashboard
      ) || {},
      demo: (
        hasValidDemo && definedDemo
      ) || { active: definedDemo || false },
      dev: (
        hasValidDev && definedDev
      ) || { active: definedDev || null },
      filters: (
        hasValidUserFilters && definedFilters
      ) || [],
      home: (
        hasValidHome && definedHome
      ) || {},
      messages: (
        hasValidMessages && definedMessages
      ) || [],
      networks: (
        hasValidNetworks && definedNetworks
      ) || [],
      reports: (
        hasValidReports && definedReports
      ) || [],
      themes: (
        hasValidThemes && definedThemes
      ) || {},
    }
  },
  normalizePermissions(assignedPermissions, role) {
    const {
      alias: permissionAlias,
      bulk: permissionBulk,
      customAnalytics: permissionCustomAnalytics,
      demo: permissionDemo,
      dev: permissionDev,
      discrepancy: permissionDiscrepancy,
      pacing: permissionPacing,
      programmatic: permissionProgrammatic,
      settings: permissionSettings,
    } = assignedPermissions || {}

    return {
      ...assignedPermissions,
      alias: typeof permissionAlias === 'boolean' && permissionAlias,
      bulk: typeof permissionBulk === 'boolean' && permissionBulk,
      customAnalytics: (
        Array.isArray(permissionCustomAnalytics) && permissionCustomAnalytics
      ) || false,
      demo: role === 'internal' || (
        typeof permissionDemo === 'boolean' && permissionDemo
      ) || false,
      dev: typeof permissionDev === 'boolean' && permissionDev,
      discrepancy: typeof permissionDiscrepancy === 'boolean' && permissionDiscrepancy,
      pacing: typeof permissionPacing === 'boolean' && permissionPacing,
      programmatic: typeof permissionProgrammatic === 'boolean' && permissionProgrammatic,
      settings: typeof permissionSettings === 'boolean' && permissionSettings,
    }
  },

  mapUser(person) {
    const {
      access: assignedAccess,
      isActive,
      isAdmin,
      isInternal,
      network: assignedNetworks = [],
      settings: assignedSettings = {},
    } = person
    const {
      active = false,
      expires = null,
      level = isAdmin ? 'admin' : 'reader',
      permissions: assignedPermissions = {},
      role = isInternal ? 'internal' : 'external',
    } = assignedAccess || {}

    const permissions = this.normalizePermissions(assignedPermissions, role)
    const settings = this.normalizeSettings(assignedSettings)
    const access = {
      ...assignedAccess,
      active: [true, false].includes(active) ? active : isActive || false,
      expires,
      level,
      permissions,
      role,
    }
    const isValidNetworkDefined = Array.isArray(assignedNetworks) && !assignedNetworks.find((network) => typeof network
      !== 'object')
    const validNetworks = isValidNetworkDefined ? assignedNetworks : []
    const network = validNetworks.map((nw) => {
      const { organization, team: teams } = nw
      const { code: billingCode, id: publisherId, networkCode = null } = organization
      return {
        organization: {
          code: billingCode,
          id: publisherId,
          networkCode,
        },
        team: teams.map((team) => {
          const { code: teamCode, id: teamId } = team

          return {
            code: teamCode,
            id: teamId,
          }
        }),
      }
    })

    return {
      access,
      network,
      settings,
    }
  },
  getAssignments(personUser, organizations) {
    return organizations.find((org) => {
      const foundAssignments = Array.isArray(org.assignments) && org.assignments.find((o) => o.email
        === personUser.email)

      return (
        foundAssignments && foundAssignments.reduce((assignments, assignment) => {
          assignments[`assignments.${assignment.billingCode}.${assignment.teamId}`] = assignment
          return assignments
        }, {})
      ) || {}
    })
  },
  async mapPersonAndUser(personUser, publishersAsTeams) {
    const {
      access: assignedAccess,
      email = personUser.docId,
      isAdmin,
      isInternal,
      personId,
      personNameFirst,
      personNameLast,
      settings: assignedSettings = {},
    } = personUser
    const {
      active,
      expires = null,
      level = isAdmin ? 'admin' : 'reader',
      permissions: assignedPermissions = {},
      role = isInternal ? 'internal' : 'external',
    } = assignedAccess || {}

    // const assignments = this.getAssignments(personUser, organizations)
    const permissions = this.normalizePermissions(assignedPermissions, role)
    const personErrors = async () => this.validatePerson(personUser)
    const userErrors = async () => this.validateUser(personUser)
    const authorizePersonErrors = this.authorizePerson(personUser)
    const authorizeUserErrors = this.authorizeUser(personUser)

    const isPersonAuthorized = authorizePersonErrors.length === 0
    const isUserAuthorized = authorizeUserErrors.length === 0
    const isUserActive = active === true

    const access = {
      ...assignedAccess,
      active: isUserActive,
      expires: expires || '2200-01-01T00:00:00Z',
      level,
      permissions,
      role,
    }

    const isUserInternal = access?.role === 'internal'
    const isUserExternal = !isUserInternal
    const inDemoMode = this.inDemoMode(personUser)
    const isPersonUserActive = this.isActiveUser(personUser)
    const isPersonUserInternal = this.isInternalUser(personUser)

    const assignedNetworks = this.mapPublisherTeamToNetwork(inDemoMode, personUser, publishersAsTeams)
    const networkIdsAndNames = {
      billingCodes: [],
      billingCodesExpired: [],
      customAnalyticsActive: [],
      customAnalyticsConfigured: [],
      customAnalyticsPossible: [],
      filters: [],
      networkCodes: [],
      orgIds: [],
      orgNames: [],
      reportCodes: [],
      teamCodes: [],
      teamIds: [],
      teamNames: [],
    }

    const hasCustomAnalytics = permissions.customAnalytics
    const userCustomAnalyticsAll = hasCustomAnalytics || []

    // @TODO Handle external user associated to org with hasAccessExpired.
    const networkIdentifiers = assignedNetworks.reduce((orgIdentifiers, network) => {
      const { associationId = {}, publisher = {}, subId } = network
      const { networkCode, orgId, teamId, teamCode, teamName } = publisher || {}

      if (orgIdentifiers.billingCodes.indexOf(associationId) === -1) {
        orgIdentifiers.billingCodes.push(associationId)
        orgIdentifiers.networkCodes.push(networkCode)
        orgIdentifiers.orgIds.push(orgId)

        orgIdentifiers.orgNames.push(publisher.text)
      }

      orgIdentifiers.reportCodes.push(`${associationId}/${subId}`)
      orgIdentifiers.teamCodes.push(`${associationId}/${teamCode}`)
      orgIdentifiers.teamIds.push(`${orgId}/${teamId}`)

      orgIdentifiers.teamNames.push(teamName || 'Team Name Unknown')

      if (hasCustomAnalytics) {
        const { customAnalytics: orgCustomAnalytics } = (
          publisher && publisher.customAnalyticsActive
        ) || {}
        const userCustomAnalyticHrefs = userCustomAnalyticsAll.map((customAnalytic) => customAnalytic.href)
        const setCustomAnalytics = () => {
          if (Array.isArray(orgCustomAnalytics) && orgCustomAnalytics.length) {
            const isPersonUserInternalAdmin = isPersonUserInternal && level === 'admin'
            const customAnalyticsConfiguredForAdmins = () => isPersonUserInternalAdmin && orgCustomAnalytics
            const customAnalyticsConfiguredForNonAdmins = () => orgCustomAnalytics.filter((cr) => userCustomAnalyticHrefs.indexOf(
              cr.href,
            ) !== -1)
            const customAnalyticsConfigured = customAnalyticsConfiguredForAdmins()
              || customAnalyticsConfiguredForNonAdmins()
            const customAnalyticsActive = customAnalyticsConfigured.filter((cr) => cr.active)

            orgIdentifiers.customAnalyticsPossible.push(...orgCustomAnalytics)
            orgIdentifiers.customAnalyticsActive.push(...customAnalyticsActive)
            orgIdentifiers.customAnalyticsConfigured.push(...customAnalyticsConfigured)
          }
        }

        if (Array.isArray(orgCustomAnalytics)) setCustomAnalytics()
      }

      // @TODO Ask Henry if there are any API props we should ref to manage access at org level.
      if (isUserExternal && network.publisher?.hasAccessExpired) {
        orgIdentifiers.billingCodesExpired.push(network.associationId)
      }

      return orgIdentifiers
    }, networkIdsAndNames)

    const hasCustomAnalyticsActive = !!networkIdentifiers.customAnalyticsActive.length
    const hasOrgCustomAnalyticErrors = async () => {
      const validateExternal = () => (
        userCustomAnalyticsAll.length
        && !networkIdentifiers.customAnalyticsConfigured.length
        && userCustomAnalyticsAll.map((
          customAnalytic,
          index,
        ) => [`organization/custom-analytics/not-found:${customAnalytic.href || index}`])
      ) || networkIdentifiers.customAnalyticsConfigured.filter((customAnalytic) => !customAnalytic.href
        || typeof customAnalytic.active
        !== 'boolean')
        .map((
          orgCustomAnalyticConfig,
          index,
        ) => [`organization/custom-analytics/misconfig/${orgCustomAnalyticConfig.href || index}`])
      return isPersonUserInternal || validateExternal()
    }
    const hasUserCustomAnalyticErrors = async () => {
      const validateExternal = () => userCustomAnalyticsAll.reduce((
        foundCustomAnalyticErrors,
        customAnalyticConfig,
        index,
      ) => {
        const foundOrgConfig = networkIdentifiers.customAnalyticsConfigured.find((customAnalytic) => customAnalytic.href
          === customAnalyticConfig.href)
        if (typeof foundOrgConfig !== 'object') {
          foundCustomAnalyticErrors.push(`user/custom-analytics/not-found:${customAnalyticConfig.href || index}`)
        } else if (typeof foundOrgConfig.active !== 'boolean') {
          foundCustomAnalyticErrors.push(`user/custom-analytics/misconfig:${customAnalyticConfig.href || index}`)
        }
        return foundCustomAnalyticErrors
      }, [])
      const validateInternal = () => isPersonUserInternal
        && userCustomAnalyticsAll.filter((customAnalytic) => typeof customAnalytic.href
          !== 'string'
          || typeof customAnalytic.active
          !== 'boolean')
          .map((customAnalytic, index) => [`user/custom-analytics/misconfig:${customAnalytic.href || index}`])

      return hasCustomAnalytics && (
        validateInternal() || validateExternal()
      )
    }

    const hasTeamsLimited = !isUserInternal && !!assignedNetworks.find((network) => {
      const { team } = network
      return Array.isArray(team) && team.find((t) => !t || typeof t !== 'object' || t.code?.toString() !== '-1')
    })

    const settings = this.normalizeSettings(assignedSettings)

    const { alert = {}, alias = {}, filters } = settings
    const hasAlerts = !alert?.dismissed
    const hasFilters = !!filters?.length

    const nameFirst = personNameFirst?.trim()
    const nameLast = personNameLast?.trim()

    const { billingCodes, billingCodesExpired } = networkIdentifiers
    const hasActiveOrgs = (
      billingCodes && billingCodes.find((billingCode) => !billingCodesExpired.includes(billingCode))
    ) || false

    const accessErrorPromises = [
      personErrors(),
      userErrors(),
      hasUserCustomAnalyticErrors(),
      hasOrgCustomAnalyticErrors(),
    ]
    const accessErrorSets = await Promise.all(accessErrorPromises)
    const hasAccessErrors = accessErrorSets.reduce((allErrors, foundErrors) => {
      if (foundErrors && foundErrors.length) allErrors.push(...foundErrors)
      return allErrors
    }, [])

    const authErrorSets = [authorizePersonErrors, authorizeUserErrors]
    const hasAuthErrors = authErrorSets.reduce((allErrors, foundErrors) => {
      if (foundErrors && foundErrors.length) allErrors.push(...foundErrors)
      return allErrors
    }, [])

    const isConfigValid = hasAccessErrors.length === 0
    const canAccessApps = isConfigValid // passes validation
      && isPersonAuthorized // is active & not deleted
      && isUserAuthorized // is active & not expired
      && (
        isUserInternal || hasActiveOrgs?.length > 0
      ) // external is related to active org

    return {
      ...personUser,

      associationId: isPersonUserInternal ? 'OAOA2001' : billingCodes[0],
      subId: email,

      baseReportName: 'person',
      reportPath: 'profile',
      routeName: 'internal',

      dataType: 'person',

      access,
      email,

      // Person and User Access Grants
      'access.active': personUser?.access?.active,
      'access.expires': personUser?.access?.expires,
      'access.level': personUser?.access?.level,
      'access.role': personUser?.access?.role,

      'access.permissions.alias': permissions.alias,
      'access.permissions.bulk': permissions.bulk,
      'access.permissions.customAnalytics': permissions.customAnalytics,
      'access.permissions.demo': permissions.demo,
      'access.permissions.dev': permissions.dev,
      'access.permissions.discrepancy': permissions.discrepancy,
      'access.permissions.pacing': permissions.pacing,
      'access.permissions.programmatic': permissions.programmatic,
      'access.permissions.settings': permissions.settings,

      // ...assignments,

      canAccessApps,
      canAdmin: access.role === 'admin',
      canRead: ['admin', 'reader', 'writer'].includes(access.role),
      canWrite: ['admin', 'writer'].includes(access.role),

      hasAccessErrors,
      hasAuthErrors,

      hasActiveOrgs,
      hasAlerts,
      hasCustomAnalytics,
      hasCustomAnalyticsActive,
      hasFilters,
      hasTeamsLimited,

      // Enabled/Disabled States
      inAliasMode: !!alias && typeof alias === 'object' && alias.active,
      inDemoMode,

      // Validation
      isConfigValid,

      // Person Properties
      isPersonAuthorized,
      // isPersonInternal: See isInternal, inherited from Person.
      isPersonUserActive,
      isPersonUserInternal,

      // User Properties
      isUserAuthorized,
      isUserActive,
      isUserInternal,

      // Assignments
      network: assignedNetworks,
      ...networkIdentifiers,

      // Identifiers
      personId,
      personName: `${nameFirst} ${nameLast}`,
      personNameFirst: nameFirst,
      personNameLast: nameLast,
      personRole: (
        personUser?.isInternal && 'internal'
      ) || 'external',

      settings,
    }
  },

  aliasId(userRecord) {
    const access = userRecord.access || {}
    const isInternalUser = access.role === 'internal'
    const hasLevelPermission = isInternalUser && ['admin', 'writer', 'reader'].indexOf(access.level) !== -1
    const hasPermissions = access.permissions && typeof access.permissions === 'object' && access.permissions.alias
    const allowAliasMode = hasLevelPermission || (
      isInternalUser && hasPermissions
    )

    const aliasSettings = userRecord.settings && userRecord.settings.alias
    const isAliasActive = aliasSettings && typeof aliasSettings === 'object' && aliasSettings.active

    return allowAliasMode && isAliasActive && aliasSettings.email
  },
}

export default user
